export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  tips: string[];
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
  { name: 'DeepSeek', url: () => 'https://chat.deepseek.com/' },
];

export const demoInstruction =
  'Raporlama ve belge hazırlama konusunda uzman bir yardımcı gibi davran. Sana verilen doğrulanmış bilgileri kullanarak profesyonel, nesnel ve düzenli bir olay raporu taslağı hazırla. Bilinmeyen bilgileri uydurma. Eksik alanları belirt ve gerekli olduğunda önce açıklayıcı sorular sor.';

export const demoOutput = {
  title: 'OLAY RAPORU',
  sections: [
    { heading: 'Genel bilgiler', lines: ['Rapor numarası', 'Tarih ve saat', 'Olay yeri', 'İlgili birim'] },
    { heading: 'Kronolojik anlatım', lines: ['Olayların verilen kaynaklara dayalı sıralaması', 'Doğrudan gözlemler ile aktarılan bilgilerin ayrılması', 'Doğrulanmamış çıkarımlardan kaçınılması'] },
    { heading: 'İşlemler ve ekler', lines: ['İlgili kişiler ve kayıtlar', 'Mevcut belgeler ve kaynak referansları', 'Delil ve ek belgelerin durumuna ilişkin bilgiler'] },
  ],
};

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Olay Raporu Anlatımı',
    text: `Raporlama ve belge düzenleme konusunda uzman bir yardımcı gibi davran. Verilen bilgileri kullanarak “Olay Raporu Anlatımı” başlıklı, profesyonel ve nesnel bir rapor taslağı hazırla.

Öncelikle olayın teknik ve zamansal çerçevesini oluştur. [Olayın kesin tarihi ve saati], [Ayrıntılı konum] ve [İlgili birim veya görev kaydı] alanlarını kullan. Bilgi eksikse tahmin yürütme; eksik alanları açıkça işaretle.

Ardından olayın başlangıç aşamasını, mevcut kayıtlarda belirtilen koşulları ve [Doğrulanmış olay bilgileri] kronolojik sırayla düzenle. Doğrudan gözlemleri, tanık veya üçüncü kişilerden aktarılan bilgileri ve çıkarımları birbirinden ayır.

Mevcut belgelerde yer alan iletişimleri, talimatları ve gerçekleştirilen işlemleri yalnızca kaynakların desteklediği ölçüde kaydet. [Kaynak kayıtları] ile [Olay zaman çizelgesi] arasında uyuşmazlık varsa bunları belirt; kendi başına bir açıklama üretme.

Müdahale, yaralanma, güç kullanımı veya delillere ilişkin bir bilgi varsa bunu yalnızca verilen kayıtların desteklediği biçimde aktar. Eksik gerekçeleri sonradan oluşturma ve gerçekleşmemiş bir işlemi gerçekleşmiş gibi gösterme.

Son olarak, mevcut belgelerin ve kayıtların durumunu özetle. [İlgili kişiler], [Mevcut ekler], [Yardım veya yönlendirme kayıtları] ve [Doğrulanması gereken bilgiler] alanlarını ayrı ayrı göster.

Raporu tamamlamadan önce eksik bilgileri, kronoloji sorunlarını ve doğrulanması gereken noktaları listele. Kritik bilgiler eksikse önce açıklayıcı sorular sor. Hiçbir tarih, olay, ifade, delil veya mevzuat referansı uydurma.`,
    tips: [
      'Tarihleri ve olay sıralamasını mevcut kayıtlarla karşılaştırın.',
      'Eksik alanları ve kaynak farklılıklarını kontrol edin.',
      'Rapordaki tüm iddiaların dayanaklarını doğrulayın.',
    ],
  },
  {
    n: 2,
    title: 'Belge ve Kronoloji İncelemesi',
    text: `Belge inceleme ve kronoloji düzenleme konusunda uzman bir yardımcı gibi davran. Görevin, sana verilen belgeleri “Belge ve Kronoloji İncelemesi” başlıklı, düzenli ve nesnel bir çalışma notuna dönüştürmektir.

Önce incelenecek kaynakları listele: [Dosya veya kayıt numarası], [İncelenecek belgeler] ve [Belgelerin tarihleri]. Her belge için kısa bir kaynak referansı oluştur.

Ardından olayları kaynaklara dayalı olarak kronolojik sıraya koy. Her adımın yanında hangi belgeye dayandığını belirt. [Olay zaman çizelgesi] içinde boşluk veya çelişki varsa bunları ayrı bir başlık altında listele.

İfadelerin ve kayıtların anlamını değiştirmeden, [Tanık ifadeleri] ile [Resmî kayıtlar] arasındaki farklılıkları belirt. Kendi yorumunu ekleme; yalnızca açıklığa kavuşturulması gereken noktaları işaretle.

Son olarak [Doğrulanması gereken bilgiler] ve eksik belgeleri listele. Kritik bilgiler eksikse önce açıklayıcı sorular sor. Hiçbir tarih, ifade veya referans uydurma.`,
    tips: [
      'Her kronoloji adımının bir kaynak belgeye dayandığından emin olun.',
      'İfadelerin anlamını değiştiren yeniden yazımlardan kaçının.',
    ],
  },
  {
    n: 3,
    title: 'Kayıtlı Bilgilerle Araç/Olay Raporu',
    text: `Raporlama ve kayıt düzenleme konusunda uzman bir yardımcı gibi davran. Görevin, verilen kayıtlı bilgileri kullanarak “Araç/Olay Raporu” başlıklı, profesyonel ve nesnel bir taslak hazırlamaktır.

Önce raporun genel bilgilerini oluştur: [İlgili birim], [İşlemin tarihi ve saati] ve [Olay yeri]. Görevli personeli yalnızca verilen kayıtlara göre belirt.

Aracın tanımlayıcı bilgilerini kayıtlarda yer aldığı şekilde aktar: [Marka], [Model], [Renk] ve [Plaka]. Bu bilgiler eksikse tahmin etme; eksik alan olarak işaretle.

Olayın gelişimini [Doğrulanmış olay bilgileri] ve [Kaynak kayıtları] doğrultusunda kronolojik olarak düzenle. Gözlemlenen hasarları, aracın durumunu ve bulunan nesneleri yalnızca kayıtların desteklediği ölçüde belirt.

Son olarak aracın teslim veya muhafaza durumunu, [Yapılan bildirimler] ve [Mevcut ekler] bilgilerini özetle. Doğrulanması gereken noktaları ayrı bir liste hâlinde göster.

Önemli: Hiçbir dosya numarası, karar, mevzuat referansı veya kaynak uydurma. Doğrulayamadığın bilgileri açıkça belirt ve resmî kaynaklardan kontrol edilmesi gereken verileri işaretle.`,
    tips: ['Mevzuat referanslarını güncel ve resmî kaynaklardan doğrulayın.'],
  },
];
